import React, { useState } from 'react';
import { Bell, ChevronDown, LayoutDashboard, Search, User, Globe, MapPin } from 'lucide-react';
import { useLiveTime } from '../hooks/useLiveTime';
import { Button } from './ui/Button';
import { cn } from '../utils/cn';
interface HeaderProps {
  currentView: 'hq' | 'site';
  siteName?: string;
  onViewChange: (view: 'hq' | 'site', siteId?: string) => void;
}
export function Header({
  currentView,
  siteName = 'Main Gate',
  onViewChange
}: HeaderProps) {
  const time = useLiveTime();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const sites = [{
    id: 'hq',
    name: 'HQ Command View',
    type: 'hq'
  }, {
    id: 's1',
    name: 'Lucknow Hub',
    type: 'site'
  }, {
    id: 's2',
    name: 'Delhi NCR',
    type: 'site'
  }, {
    id: 's3',
    name: 'Mumbai Port',
    type: 'site'
  }];
  return <header className="sticky top-0 z-30 flex h-16 w-full items-center justify-between border-b border-gray-800 bg-[#0f1115]/95 px-6 backdrop-blur supports-[backdrop-filter]:bg-[#0f1115]/80" data-id="element-108">
      {/* Left: Branding */}
      <div className="flex items-center gap-4" data-id="element-109">
        <div className="flex items-center gap-2" data-id="element-110">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-amber-500 font-bold text-black" data-id="element-111">
            Q
          </div>
          <div className="flex flex-col" data-id="element-112">
            <span className="text-sm font-bold leading-none text-white" data-id="element-113">
              Quantiq
            </span>
            <span className="text-xs text-gray-400" data-id="element-114">by Qubix</span>
          </div>
        </div>
        <div className="h-6 w-px bg-gray-800" data-id="element-115" />
        <span className="text-sm font-medium text-gray-300" data-id="element-116">
          Nanak Logistics
        </span>
      </div>

      {/* Center: Gate Selector */}
      <div className="flex items-center gap-4" data-id="element-117">
        <div className="relative" data-id="element-118">
          <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-2 rounded-md border border-gray-700 bg-gray-900 px-4 py-1.5 text-sm text-gray-200 hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-500 min-w-[200px] justify-between transition-all" data-id="element-119">
            <div className="flex items-center gap-2" data-id="element-120">
              {currentView === 'hq' ? <Globe className="h-4 w-4 text-amber-500" data-id="element-121" /> : <span className="h-2 w-2 rounded-full bg-green-500" data-id="element-122" />}
              {currentView === 'hq' ? 'HQ Command View' : siteName}
            </div>
            <ChevronDown className={cn('h-4 w-4 text-gray-500 transition-transform', isDropdownOpen && 'rotate-180')} data-id="element-123" />
          </button>

          {isDropdownOpen && <div className="absolute top-full left-0 mt-1 w-full bg-[#1a1d23] border border-gray-800 rounded-md shadow-xl overflow-hidden z-50" data-id="element-124">
              <div className="py-1" data-id="element-125">
                {sites.map(site => <button key={site.id} onClick={() => {
              onViewChange(site.type as 'hq' | 'site', site.id === 'hq' ? undefined : site.id);
              setIsDropdownOpen(false);
            }} className={cn('w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-800 transition-colors', currentView === 'hq' && site.id === 'hq' || currentView === 'site' && siteName === site.name ? 'text-amber-500 bg-gray-800/50' : 'text-gray-300')} data-id="element-126">
                    {site.type === 'hq' ? <Globe className="h-3.5 w-3.5" data-id="element-127" /> : <MapPin className="h-3.5 w-3.5" data-id="element-128" />}
                    {site.name}
                  </button>)}
              </div>
            </div>}
        </div>
      </div>

      {/* Right: System Status & User */}
      <div className="flex items-center gap-6" data-id="element-129">
        {/* System Health */}
        <div className="flex items-center gap-2 rounded-full bg-gray-900 px-3 py-1 border border-gray-800" data-id="element-130">
          <span className="relative flex h-2.5 w-2.5" data-id="element-131">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" data-id="element-132"></span>
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" data-id="element-133"></span>
          </span>
          <span className="text-xs font-medium text-gray-300" data-id="element-134">
            System Normal
          </span>
        </div>

        {/* Clock */}
        <div className="font-mono text-sm font-medium text-gray-400" data-id="element-135">
          {time.toLocaleTimeString('en-IN', {
          hour12: false
        })}
        </div>

        <div className="h-6 w-px bg-gray-800" data-id="element-136" />

        {/* User Profile */}
        <div className="flex items-center gap-3" data-id="element-137">
          <div className="text-right hidden md:block" data-id="element-138">
            <div className="text-sm font-medium text-white" data-id="element-139">Vikram S.</div>
            <div className="text-xs text-gray-500" data-id="element-140">Supervisor</div>
          </div>
          <div className="h-9 w-9 overflow-hidden rounded-full bg-gray-800 border border-gray-700" data-id="element-141">
            <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80" alt="User" className="h-full w-full object-cover" data-id="element-142" />
          </div>
        </div>
      </div>
    </header>;
}